import { useEffect, useState } from 'react';

import { knownErrors } from '~/schema/project';
import { useProjectWorksheetContext } from '~/features/projectWizard/worksheets/project/providers/ProjectWorksheetProvider';
import FieldErrorCount from './FieldErrorCount';
import { usePageHeaderContext } from '~/components/PageHeader/PageHeaderProvider';
import { useProjectContext } from '~/hooks';
import './styles.scss';

type KnownErrorCategories = Record<string, string[]>;

function scrollToAnchor(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function scrollToAnchorAfterRender(id: string) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => scrollToAnchor(id));
  });
}

// Highlights the section most recently scrolled past. We pick the last id
// whose top is above a small offset from the viewport top; before any section
// has scrolled past we fall back to the first one.
function useActiveSection(ids: string[], offsetPx = 140) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (ids.length === 0) return;

    const compute = () => {
      let current: string | null = ids[0] ?? null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - offsetPx <= 0) {
          current = id;
        } else {
          break;
        }
      }
      setActiveId(current);
    };

    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, [ids.join('|'), offsetPx]);

  return activeId;
}

export default function ProjectWorksheetHeader() {
  const pageHeaderContext = usePageHeaderContext();
  const { config, isSectionCollapsed, expandSection, actionBarHeight, isTocExpanded, toggleTocExpanded } = useProjectWorksheetContext();
  const {
    conditions: {
      sectionLabel,
      subsectionLabel,
      isSubsectionDisplaying
    }
  } = useProjectContext();

  const allIds = config.flatMap(section => [
    section.id,
    ...section.subsections
      .filter(sub => isSubsectionDisplaying(sub.id))
      .map(sub => `${section.id}-${sub.id}`)
  ]);
  const activeId = useActiveSection(allIds);


  return (
    <nav
      className="card worksheet-toc"
      aria-label="Worksheet sections"
      style={{
        top: `${((pageHeaderContext.height ?? 0) + actionBarHeight) / 16}rem`
      }}
    >
      <ol className="worksheet-toc__list">
        {config.map(section => {
          const categories = knownErrors[section.id] as KnownErrorCategories;
          const sectionErrorFields = Object.values(categories ?? {}).flatMap(fields =>
            Array.isArray(fields) ? fields : []
          );
          const isSectionActive = activeId === section.id;
          const visibleSubs = section.subsections.filter(sub => isSubsectionDisplaying(sub.id));

          const expandedNow = isTocExpanded(section.id);

          const hasSubs = visibleSubs.length > 0;
          return (
            <li key={section.id} className="worksheet-toc__section">
              <button
                type="button"
                className={`worksheet-toc__row worksheet-toc__row--accordion${isSectionActive ? ' active' : ''}`}
                aria-expanded={expandedNow}
                disabled={!hasSubs}
                onClick={() => hasSubs && toggleTocExpanded(section.id)}
              >
                {hasSubs ? (
                  <span
                    className={`worksheet-toc__caret${expandedNow ? ' expanded' : ''}`}
                    aria-hidden="true"
                  >
                    <svg
                      className="worksheet-toc__caret-icon"
                      width="10"
                      height="10"
                      viewBox="0 0 10 10"
                    >
                      <path d="M3.5 1.5 L7 5 L3.5 8.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                ) : (
                  <span className="worksheet-toc__caret worksheet-toc__caret--placeholder" />
                )}
                <span className="worksheet-toc__link worksheet-toc__link--accordion">
                  <span>{sectionLabel(section.id, section.label)}</span>
                  <FieldErrorCount categoryData={sectionErrorFields} showSuccessState />
                </span>
              </button>
              {expandedNow && visibleSubs.length > 0 && (
                <ol className="worksheet-toc__sublist">
                  {visibleSubs.map(sub => {
                    const subDomId = `${section.id}-${sub.id}`;
                    const isSubActive = activeId === subDomId;
                    const subErrorFields = (categories?.[sub.id] as string[] | undefined) ?? [];
                    return (
                      <li key={sub.id} className="worksheet-toc__subsection">
                        <button
                          type="button"
                          className={`worksheet-toc__link worksheet-toc__link--sub${isSubActive ? ' active' : ''}`}
                          onClick={() => {
                            if (isSectionCollapsed(section.id)) {
                              expandSection(section.id);
                              scrollToAnchorAfterRender(subDomId);
                            } else {
                              scrollToAnchor(subDomId);
                            }
                          }}
                        >
                          <span>{subsectionLabel(sub.id, sub.label)}</span>
                          <FieldErrorCount categoryData={subErrorFields} showSuccessState />
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
